<template>
  <div class="login-page" :class="{ 'dark-mode': isDarkMode }">
    <div class="login-container">
      <div class="login-left">
        <div class="brand-section">
          <div class="logo-wrapper">
            <img src="/images/logo.png" alt="Logo" class="logo-img">
          </div>
          <h1 class="brand-title">资源云共享</h1>
          <p class="brand-slogan">ShareFree Cloud</p>
          <p class="brand-description">云端资源共享，畅享无限可能</p>
        </div>
      </div>
      
      <div class="login-right">
        <el-card class="login-card" shadow="always">
          <div class="login-header">
            <h2>欢迎回来</h2>
            <p>资源云共享 - 登录您的账户</p>
          </div>
          
          <el-form :model="loginForm" :rules="rules" ref="loginForm" class="login-form" autocomplete="off">
            <el-form-item prop="username">
              <el-input 
                v-model="loginForm.username" 
                placeholder="请输入用户名或邮箱"
                prefix-icon="el-icon-user"
                size="large"
                class="login-input"
                name="username"
                :autocomplete="rememberMe ? 'username' : 'off'">
              </el-input>
            </el-form-item>
            
            <el-form-item prop="password">
              <el-input 
                v-model="loginForm.password" 
                type="password"
                placeholder="请输入密码"
                prefix-icon="el-icon-lock"
                show-password
                size="large"
                class="login-input"
                name="password"
                :autocomplete="rememberMe ? 'current-password' : 'off'"
                @keyup.enter.native="handleLogin">
              </el-input>
            </el-form-item>
            
            <div class="form-options">
              <el-checkbox v-model="rememberMe">记住我</el-checkbox>
              <el-link type="primary" href="#">忘记密码？</el-link>
            </div>
            
            <el-form-item>
              <el-button 
                type="primary" 
                @click="handleLogin" 
                :loading="loading"
                size="large"
                class="login-button"
                round>
                登录
              </el-button>
            </el-form-item>
            
            <div class="divider" v-if="wechatEnabled">
              <span>或使用其他方式登录</span>
            </div>
            
            <el-form-item v-if="wechatEnabled">
              <div class="social-login">
                <el-button 
                  type="success" 
                  @click="handleWechatLogin" 
                  :loading="wechatLoading"
                  size="medium"
                  class="social-btn wechat-btn"
                  icon="el-icon-chat-dot-round">
                  微信登录
                </el-button>
              </div>
            </el-form-item>
            
            <div class="login-footer">
              <span>还没有账号？</span>
              <el-link type="primary" @click="goRegister">立即注册</el-link>
            </div>
          </el-form>
        </el-card>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Login',
  data() {
    return {
      loginForm: {
        username: '',
        password: ''
      },
      rules: {
        username: [
          { required: true, message: '请输入用户名或邮箱', trigger: 'blur' }
        ],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' }
        ]
      },
      loading: false,
      wechatLoading: false,
      wechatEnabled: false,  // 微信登录是否启用
      rememberMe: false,
      isDarkMode: false,
      themeConfig: {
        theme_primary_color: '#667eea'
      }
    }
  },
  mounted() {
    // 如果之前选择了记住用户名，则自动填充
    const rememberedUsername = localStorage.getItem('remembered_username')
    if (rememberedUsername) {
      this.loginForm.username = rememberedUsername
      this.rememberMe = true
    }
    
    // 读取主题配置
    this.loadThemeConfig()
    
    // 读取深色模式状态
    this.loadDarkMode()
    
    // 检查微信登录是否启用
    this.checkWechatLoginStatus()
  },
  methods: {
    loadDarkMode() {
      try {
        const themeConfig = localStorage.getItem('theme_config')
        console.log('登录页-读取到的主题配置:', themeConfig)
        if (themeConfig) {
          const config = JSON.parse(themeConfig)
          console.log('登录页-解析后的配置:', config)
          console.log('登录页-theme_mode值:', config.theme_mode)
          this.isDarkMode = config.theme_mode === 'dark'
          console.log('登录页-设置isDarkMode为:', this.isDarkMode)
        } else {
          console.log('登录页-未找到theme_config，使用默认浅色模式')
        }
      } catch (error) {
        console.error('登录页-读取深色模式状态失败:', error)
      }
    },
    async loadThemeConfig() {
      try {
        const res = await this.$axios.get('/config/display')
        if (res.data.code === 200 && res.data.data) {
          const config = res.data.data
          this.themeConfig = {
            theme_primary_color: config.themePrimaryColor || '#667eea'
          }
        }
      } catch (error) {
        console.error('加载主题配置失败:', error)
        // 使用默认配置
        this.themeConfig = {
          theme_primary_color: '#667eea'
        }
      }
    },
    
    async checkWechatLoginStatus() {
      try {
        // 尝试获取微信授权URL，如果失败说明未启用
        const res = await this.$axios.get('/auth/wechat/authorize-url')
        this.wechatEnabled = res.data.code === 200
      } catch (error) {
        // 微信登录未启用或者错误
        this.wechatEnabled = false
      }
    },
    async handleLogin() {
      this.$refs.loginForm.validate(async (valid) => {
        if (valid) {
          try {
            this.loading = true
            const res = await this.$axios.post('/auth/login', this.loginForm)
            
            if (res.data.code === 200) {
              const data = res.data.data
              localStorage.setItem('token', data.token)
              localStorage.setItem('user_info', JSON.stringify(data))
              
              // 如果选择了记住我，保存用户名到localStorage
              if (this.rememberMe) {
                localStorage.setItem('remembered_username', this.loginForm.username)
              } else {
                localStorage.removeItem('remembered_username')
              }
              
              this.$message.success('登录成功')
              
              // 避免重复导航错误
              if (this.$route.path !== '/') {
                this.$router.push('/').catch(err => {
                  // 忽略NavigationDuplicated错误，但重新抛出其他错误
                  if (err.name !== 'NavigationDuplicated') {
                    throw err
                  }
                }).then(() => {
                  // 路由跳转完成后再刷新页面
                  location.reload()
                })
              } else {
                // 如果已经在首页，直接刷新页面
                location.reload()
              }
            } else {
              this.$message.error(res.data.message)
            }
          } catch (error) {
            this.$message.error('登录失败')
          } finally {
            this.loading = false
          }
        }
      })
    },
    goRegister() {
      // 避免重复导航错误
      if (this.$route.path !== '/register') {
        this.$router.push('/register').catch(err => {
          // 忽略NavigationDuplicated错误，但重新抛出其他错误
          if (err.name !== 'NavigationDuplicated') {
            throw err
          }
        })
      }
    },
    
    async handleWechatLogin() {
      try {
        this.wechatLoading = true
        
        // 获取微信授权URL
        const res = await this.$axios.get('/auth/wechat/authorize-url')
        
        if (res.data.code === 200) {
          const authorizeUrl = res.data.data.authorizeUrl
          
          // 使用弹窗打开微信扫码页面
          const width = 800
          const height = 600
          const left = (window.screen.width - width) / 2
          const top = (window.screen.height - height) / 2
          
          const wechatWindow = window.open(
            authorizeUrl,
            'wechat_login',
            `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
          )
          
          // 监听弹窗消息
          const messageHandler = (event) => {
            // 验证消息来源
            if (event.origin !== window.location.origin) {
              return
            }
            
            if (event.data && event.data.type === 'wechat_login_success') {
              // 关闭弹窗
              if (wechatWindow && !wechatWindow.closed) {
                wechatWindow.close()
              }
              
              // 保存用户信息
              const userData = event.data.data
              localStorage.setItem('token', userData.token)
              localStorage.setItem('user_info', JSON.stringify(userData))
              
              this.$message.success('微信登录成功')
              
              // 移除监听器
              window.removeEventListener('message', messageHandler)
              
              // 跳转到首页
              this.$router.push('/').catch(err => {
                if (err.name !== 'NavigationDuplicated') {
                  throw err
                }
              }).then(() => {
                location.reload()
              })
            } else if (event.data && event.data.type === 'wechat_login_failed') {
              this.$message.error('微信登录失败: ' + event.data.message)
              window.removeEventListener('message', messageHandler)
            }
          }
          
          window.addEventListener('message', messageHandler)
          
          // 5分钟后自动移除监听器
          setTimeout(() => {
            window.removeEventListener('message', messageHandler)
          }, 300000)
        } else {
          this.$message.error(res.data.message || '获取微信授权地址失败')
        }
      } catch (error) {
        console.error('微信登录错误:', error)
        this.$message.error('微信登录失败')
      } finally {
        this.wechatLoading = false
      }
    }
  }
}
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: var(--theme-bg, white);
  position: relative;
  overflow: hidden;
}

.login-page::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 20% 50%, rgba(102, 126, 234, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 80% 80%, rgba(118, 75, 162, 0.1) 0%, transparent 50%);
  pointer-events: none;
}

.login-container {
  width: 100%;
  max-width: 900px;
  height: 600px;
  display: flex;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  position: relative;
  z-index: 1;
}

.login-left {
  flex: 1;
  background: linear-gradient(135deg, var(--theme-primary, #667eea) 0%, var(--theme-primary-dark, #764ba2) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  position: relative;
  overflow: hidden;
}

.login-left::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(102, 126, 234, 0.1) 0%, transparent 70%);
  animation: rotate 20s linear infinite;
  z-index: 0;
}

.brand-section {
  text-align: center;
  z-index: 1;
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  transform: translateY(-40px);
}

.logo-wrapper {
  margin-bottom: 15px;
}

.logo-img {
  width: 250px;
  height: 150px;
  object-fit: contain;
  animation: float 3s ease-in-out infinite;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
}

.brand-title {
  font-size: 32px;
  font-weight: 700;
  margin: 0 0 10px 0;
  background: linear-gradient(135deg, white 0%, #f0f0f0 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: none;
}

.brand-slogan {
  font-size: 18px;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 20px 0;
  font-weight: 500;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.brand-description {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
  line-height: 1.6;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.login-right {
  width: 400px;
  background: var(--theme-bg, white);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

.login-card {
  width: 100%;
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  border: none;
  overflow: hidden;
  background: var(--theme-bg, white);
}

.login-header {
  text-align: center;
  margin-bottom: 30px;
}

.login-header h2 {
  margin: 0 0 8px 0;
  font-size: 28px;
  font-weight: 700;
  color: var(--theme-text, #303133);
}

.login-header p {
  margin: 0;
  color: var(--theme-text, #909399);
  font-size: 14px;
  opacity: 0.7;
}

.login-form {
  padding: 0 20px;
}

.login-input ::v-deep .el-input__inner {
  height: 50px;
  line-height: 50px;
  border-radius: 10px;
  padding-left: 45px;
  font-size: 16px;
  border: 2px solid var(--theme-text, #e4e7ed);
  transition: all 0.3s;
  opacity: 0.7;
}

.login-input ::v-deep .el-input__inner:focus {
  border-color: var(--theme-primary, #667eea);
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.login-input ::v-deep .el-input__prefix {
  left: 15px;
}

.login-input ::v-deep .el-input__prefix i {
  font-size: 20px;
  color: var(--theme-text, #909399);
  opacity: 0.7;
}

.form-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
}

.form-options ::v-deep .el-checkbox__input.is-checked .el-checkbox__inner {
  background-color: var(--theme-primary, #667eea);
  border-color: var(--theme-primary, #667eea);
}

.form-options ::v-deep .el-checkbox__input.is-checked + .el-checkbox__label {
  color: var(--theme-text, #606266);
  opacity: 0.8;
}

.form-options ::v-deep .el-link {
  font-size: 14px;
}

.login-button {
  width: 100%;
  height: 50px;
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 1px;
  border-radius: 10px;
  background: linear-gradient(135deg, var(--theme-primary, #667eea) 0%, var(--theme-primary-dark, #764ba2) 100%);
  border: none;
  transition: all 0.3s;
}

.login-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 25px rgba(102, 126, 234, 0.3);
}

.social-login {
  display: flex;
  justify-content: center;
}

.social-btn {
  width: 100%;
  height: 45px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.wechat-btn {
  background: linear-gradient(135deg, var(--theme-primary, #667eea) 0%, var(--theme-primary-dark, #764ba2) 100%);
  border: none;
  color: white;
}

.wechat-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(7, 193, 96, 0.3);
}

.login-footer {
  text-align: center;
  margin-top: 25px;
  font-size: 14px;
  color: var(--theme-text, #606266);
  opacity: 0.8;
}

.login-footer ::v-deep .el-link {
  font-size: 14px;
  font-weight: 500;
}

.divider {
  position: relative;
  text-align: center;
  margin: 30px 0;
}

.divider::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  width: 100%;
  height: 1px;
  background: linear-gradient(90deg, transparent, var(--theme-text, #dcdfe6), transparent);
  opacity: 0.3;
}

.divider span {
  position: relative;
  display: inline-block;
  padding: 0 20px;
  background: var(--theme-bg, #ffffff);
  color: var(--theme-text, #909399);
  font-size: 14px;
  z-index: 1;
  opacity: 0.7;
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* 深色模式适配 */
.dark-mode .login-page {
  background: var(--theme-bg, #1a1a1a) !important;
}

.dark-mode .login-left {
  background: linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%) !important;
}

.dark-mode .login-left::before {
  background: radial-gradient(circle, rgba(102, 126, 234, 0.15) 0%, transparent 70%) !important;
}

.dark-mode .login-right {
  background: var(--theme-bg, #1a1a1a) !important;
}

.dark-mode .login-card {
  background: var(--theme-bg, #1a1a1a) !important;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3) !important;
}

.dark-mode .login-input ::v-deep .el-input__inner {
  background: rgba(255, 255, 255, 0.08) !important;
  border-color: rgba(255, 255, 255, 0.15) !important;
  color: var(--theme-text, #e0e0e0) !important;
}

.dark-mode .login-input ::v-deep .el-input__inner:focus {
  background: rgba(255, 255, 255, 0.12) !important;
  border-color: var(--theme-primary, #667eea) !important;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.2) !important;
}

.dark-mode .login-input ::v-deep .el-input__inner::placeholder {
  color: #8a8a8a !important;
}

.dark-mode .login-input ::v-deep .el-input__prefix i {
  color: var(--theme-primary, #8ea4f5) !important;
}

.dark-mode .login-button {
  background: linear-gradient(135deg, var(--theme-primary, #667eea) 0%, var(--theme-primary-dark, #764ba2) 100%) !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3) !important;
}

.dark-mode .login-button:hover {
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4) !important;
}

.dark-mode .wechat-btn {
  background: linear-gradient(135deg, var(--theme-primary, #667eea) 0%, var(--theme-primary-dark, #764ba2) 100%) !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3) !important;
}

.dark-mode .wechat-btn:hover {
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4) !important;
}

.dark-mode .login-header h2 {
  color: var(--theme-text, #e8e8e8) !important;
}

.dark-mode .login-header p {
  color: #8a8a8a !important;
}

.dark-mode .form-options ::v-deep .el-checkbox__input.is-checked .el-checkbox__inner {
  background-color: var(--theme-primary, #667eea) !important;
  border-color: var(--theme-primary, #667eea) !important;
}

.dark-mode .form-options ::v-deep .el-checkbox__input.is-checked + .el-checkbox__label {
  color: var(--theme-text, #e8e8e8) !important;
}

.dark-mode .form-options ::v-deep .el-link {
  color: var(--theme-primary, #8ea4f5) !important;
}

.dark-mode .form-options ::v-deep .el-link:hover {
  color: #a8bbf7 !important;
}

.dark-mode .login-footer {
  color: #8a8a8a !important;
}

.dark-mode .login-footer ::v-deep .el-link {
  color: var(--theme-primary, #8ea4f5) !important;
}

.dark-mode .login-footer ::v-deep .el-link:hover {
  color: #a8bbf7 !important;
}

.dark-mode .divider::before {
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent) !important;
}

.dark-mode .divider span {
  background: var(--theme-bg, #1a1a1a) !important;
  color: #8a8a8a !important;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .login-container {
    flex-direction: column;
    height: auto;
    max-width: 90%;
  }
  
  .login-left {
    padding: 30px 20px;
  }
  
  .login-right {
    width: 100%;
    padding: 30px 20px;
  }
  
  .brand-section {
    transform: translateY(-20px);
  }
  
  .brand-title {
    font-size: 28px;
  }
  
  .logo-img {
    width: 200px;
    height: 120px;
  }
}
</style>

<style>
/* 深色模式全局样式覆盖 - 使用最强优先级 */
body .login-page.dark-mode .login-input .el-input__inner,
body .login-page.dark-mode .el-input .el-input__inner,
body .login-page.dark-mode input.el-input__inner,
body .login-page.dark-mode .el-input.is-active .el-input__inner,
body .login-page.dark-mode .el-input--suffix .el-input__inner,
body .login-page.dark-mode .el-input--prefix .el-input__inner,
body .login-page.dark-mode .el-form-item .el-input__inner {
  background-color: #2a2a2a !important;
  background-image: none !important;
  background: #2a2a2a !important;
  border-color: #404040 !important;
  color: #e0e0e0 !important;
}

body .login-page.dark-mode .login-input .el-input__inner::placeholder,
body .login-page.dark-mode .el-input .el-input__inner::placeholder,
body .login-page.dark-mode input.el-input__inner::placeholder {
  color: #8a8a8a !important;
}

/* 确保输入框在所有状态下都保持深色背景 */
body .login-page.dark-mode .el-input__inner,
body .login-page.dark-mode .el-input__inner:hover,
body .login-page.dark-mode .el-input__inner:focus,
body .login-page.dark-mode .el-input__inner:active,
body .login-page.dark-mode .el-input.is-focus .el-input__inner,
body .login-page.dark-mode .el-input:hover .el-input__inner {
  background-color: #2a2a2a !important;
  background-image: none !important;
  background: #2a2a2a !important;
  border-color: #404040 !important;
}

/* 特别针对输入内容后的状态 */
body .login-page.dark-mode input[type="text"],
body .login-page.dark-mode input[type="password"],
body .login-page.dark-mode input[type="email"] {
  background-color: #2a2a2a !important;
  background: #2a2a2a !important;
  color: #e0e0e0 !important;
}

/* 覆盖浏览器自动填充的背景色 */
body .login-page.dark-mode input:-webkit-autofill,
body .login-page.dark-mode input:-webkit-autofill:hover,
body .login-page.dark-mode input:-webkit-autofill:focus,
body .login-page.dark-mode input:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0 1000px #2a2a2a inset !important;
  box-shadow: 0 0 0 1000px #2a2a2a inset !important;
  -webkit-text-fill-color: #e0e0e0 !important;
  caret-color: #e0e0e0 !important;
  transition: background-color 5000s ease-in-out 0s !important;
}

body .login-page.dark-mode .el-input__inner:-webkit-autofill,
body .login-page.dark-mode .el-input__inner:-webkit-autofill:hover,
body .login-page.dark-mode .el-input__inner:-webkit-autofill:focus,
body .login-page.dark-mode .el-input__inner:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0 1000px #2a2a2a inset !important;
  box-shadow: 0 0 0 1000px #2a2a2a inset !important;
  -webkit-text-fill-color: #e0e0e0 !important;
  caret-color: #e0e0e0 !important;
  transition: background-color 5000s ease-in-out 0s !important;
}

body .login-page.dark-mode .login-button.el-button--primary,
body .login-page.dark-mode button.el-button--primary,
body .login-page.dark-mode .el-button--primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  background-color: #667eea !important;
  border: none !important;
  color: white !important;
}

body .login-page.dark-mode .wechat-btn.el-button--success,
body .login-page.dark-mode button.el-button--success,
body .login-page.dark-mode .el-button--success {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  background-color: #667eea !important;
  border: none !important;
  color: white !important;
}

body .login-page.dark-mode .login-card.el-card,
body .login-page.dark-mode .el-card {
  background: #1a1a1a !important;
  background-color: #1a1a1a !important;
  border-color: #404040 !important;
}

/* 深色模式下的链接样式 */
body .login-page.dark-mode .el-link,
body .login-page.dark-mode .el-link.el-link--primary {
  color: var(--theme-primary, #8ea4f5) !important;
}

body .login-page.dark-mode .el-link:hover,
body .login-page.dark-mode .el-link.el-link--primary:hover {
  color: #a8bbf7 !important;
}
</style>